import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Card, 
  Button, 
  Space, 
  Avatar, 
  Tag, 
  Divider, 
  Row, 
  Col, 
  Tooltip,
  Modal,
  message,
  Spin,
  Breadcrumb
} from 'antd';
import { 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  UserOutlined, 
  MailOutlined, 
  CalendarOutlined, 
  ArrowLeftOutlined,
  SafetyCertificateOutlined,
  EyeOutlined,
  DownloadOutlined,
  FileTextOutlined,
  GlobalOutlined,
  IdcardOutlined,
  HomeOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAdmin } from '../../hooks/useAdmin';
import moment from 'moment';

const { Title, Text, Paragraph } = Typography;

const KYCDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { verifyKYC, loading: actionLoading } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [kycData, setKycData] = useState(null);

  useEffect(() => {
    // In a real app, you would fetch this from the API
    // const fetchKYC = async () => { ... }
    
    // Mocking for now to match the existing simulation but with better structure
    setTimeout(() => {
      setKycData({
        id: id,
        user: {
          username: 'Fouad',
          email: 'fouad@example.com',
          joinedAt: '2024-03-15',
          country: 'France'
        },
        documents: [
          { type: 'ID Card (Front)', status: 'Pending', uploadedAt: '2024-04-20 14:30', url: '#' },
          { type: 'ID Card (Back)', status: 'Pending', uploadedAt: '2024-04-20 14:32', url: '#' },
          { type: 'Proof of Address', status: 'Pending', uploadedAt: '2024-04-20 14:40', url: '#' }
        ],
        status: 'pending'
      });
      setLoading(false);
    }, 1000);
  }, [id]);

  const handleAction = async (status) => {
    const actionText = status === 'verified' ? t('manager.kyc.actions.approve') : t('manager.kyc.actions.reject');
    
    Modal.confirm({
      title: `${actionText} KYC?`,
      content: status === 'rejected' ? t('admin.user_modals.warning_desc') : '',
      okText: actionText,
      okType: status === 'verified' ? 'primary' : 'danger',
      className: 'dark-modal',
      onOk: async () => {
        try {
          const res = await verifyKYC(id, { status, notes: `Processed by manager` });
          if (res.success) {
            message.success(status === 'verified' ? t('manager.kyc.messages.approve_success') : t('manager.kyc.messages.reject_success'));
            navigate('/manager/kyc');
          } else {
            message.error(res.message || t('common.error'));
          }
        } catch (err) {
          message.error(t('common.error'));
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <Space direction="vertical" align="center">
          <Spin size="large" />
          <Text className="text-slate-400 animate-pulse uppercase tracking-[0.2em] text-xs">{t('common.loading')}</Text>
        </Space>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 min-h-screen bg-slate-950 text-slate-200 animate-fade-in">
      {/* Navigation & Breadcrumbs */}
      <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <Space direction="vertical" size={4}>
          <Breadcrumb 
            className="mb-2"
            items={[
              { title: <Link to="/manager" className="text-slate-500 hover:text-white transition-colors">{t('manager.dashboard.title')}</Link> },
              { title: <Link to="/manager/kyc" className="text-slate-500 hover:text-white transition-colors">{t('manager.kyc.title')}</Link> },
              { title: <Text className="text-indigo-400 font-bold uppercase tracking-widest text-[10px]">{t('manager.kyc.actions.view')}</Text> }
            ]}
          />
          <Space align="center">
            <Button 
              icon={<ArrowLeftOutlined />} 
              type="link" 
              onClick={() => navigate('/manager/kyc')}
              className="text-slate-400 hover:text-white p-0 mr-2"
            />
            <Title level={2} className="m-0 !text-white !font-black uppercase tracking-tight">
              {t('manager.kyc.actions.view')}: <span className="text-indigo-500">{kycData.user.username}</span>
            </Title>
          </Space>
        </Space>

        <Space>
          <Button 
            icon={<CloseCircleOutlined />} 
            onClick={() => handleAction('rejected')}
            className="btn-pro bg-rose-500/10 text-rose-500 border-rose-500/20 hover:bg-rose-500/20"
          >
            {t('manager.kyc.actions.reject')}
          </Button>
          <Button 
            type="primary"
            icon={<CheckCircleOutlined />} 
            onClick={() => handleAction('verified')}
            className="btn-pro btn-primary"
          >
            {t('manager.kyc.actions.approve')}
          </Button>
        </Space>
      </div>

      <Row gutter={[24, 24]}>
        {/* User Sidebar Info */}
        <Col xs={24} lg={8}>
          <Card className="trading-card border-none overflow-hidden relative">
            <div className="absolute top-0 right-0 p-4 opacity-5 text-6xl">
              <SafetyCertificateOutlined />
            </div>
            
            <div className="flex flex-col items-center text-center mb-8">
              <Avatar size={100} className="bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 mb-4 flex items-center justify-center">
                <UserOutlined style={{ fontSize: 40 }} />
              </Avatar>
              <Title level={3} className="!text-white !m-0 !font-black uppercase tracking-tighter">{kycData.user.username}</Title>
              <Tag color="orange" className="mt-2 font-black uppercase tracking-widest text-[10px] border-none bg-orange-500/10 text-orange-400">
                {t('admin.users_management.table.kyc.pending')}
              </Tag>
            </div>

            <Space direction="vertical" className="w-full" size={16}>
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-white/5">
                <Space>
                  <MailOutlined className="text-slate-500" />
                  <Text className="text-slate-400 text-xs font-bold uppercase">{t('manager.dashboard.table.email')}</Text>
                </Space>
                <Text className="text-white text-xs">{kycData.user.email}</Text>
              </div>

              <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-white/5">
                <Space>
                  <GlobalOutlined className="text-slate-500" />
                  <Text className="text-slate-400 text-xs font-bold uppercase">{t('manager.dashboard.table.country')}</Text>
                </Space>
                <Text className="text-white text-xs">{kycData.user.country || 'N/A'}</Text>
              </div>

              <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-white/5">
                <Space>
                  <CalendarOutlined className="text-slate-500" />
                  <Text className="text-slate-400 text-xs font-bold uppercase">{t('manager.dashboard.table.date')}</Text>
                </Space>
                <Text className="text-white text-xs">{kycData.user.joinedAt}</Text>
              </div>
              
              <Divider className="border-white/5 m-0" />
              
              <Button 
                block 
                className="bg-white/5 border-white/10 text-slate-400 hover:text-white hover:bg-white/10"
                onClick={() => navigate(`/admin/users/${id}`)}
              >
                {t('admin.users_management.table.actions.view')}
              </Button>
            </Space>
          </Card>
        </Col>

        {/* Documents Content */}
        <Col xs={24} lg={16}>
          <Card 
            className="trading-card border-none h-full"
            title={<Text className="text-white font-bold uppercase tracking-widest text-xs flex items-center gap-2"><IdcardOutlined className="text-indigo-500" /> {t('manager.kyc.subtitle')}</Text>}
          >
            <div className="space-y-4">
              {kycData.documents.map((doc, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-slate-900/50 rounded-2xl border border-white/5 hover:border-indigo-500/30 transition-all group">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-slate-800 rounded-xl group-hover:bg-indigo-500/10 transition-colors">
                      <FileTextOutlined className="text-xl text-slate-500 group-hover:text-indigo-400" />
                    </div>
                    <div>
                      <Text className="text-sm font-bold text-white block tracking-wide">{doc.type}</Text>
                      <Text className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mt-1">
                        {t('manager.kyc.table.submitted')}: {doc.uploadedAt}
                      </Text>
                    </div>
                  </div>
                  
                  <Space>
                    <Tooltip title={t('manager.kyc.actions.view')}>
                      <Button 
                        icon={<EyeOutlined />} 
                        className="bg-white/5 border-white/10 text-slate-400 hover:text-white"
                      />
                    </Tooltip>
                    <Tooltip title="Download">
                      <Button 
                        icon={<DownloadOutlined />} 
                        className="bg-white/5 border-white/10 text-slate-400 hover:text-white"
                      />
                    </Tooltip>
                  </Space>
                </div>
              ))}
            </div>

            {/* Document Preview Placeholder */}
            <div className="mt-8 p-12 bg-slate-950 border border-white/5 rounded-3xl flex flex-col items-center justify-center aspect-video text-center group hover:border-indigo-500/20 transition-colors border-dashed">
              <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <EyeOutlined className="text-3xl text-slate-700 group-hover:text-indigo-500 transition-colors" />
              </div>
              <Text className="text-slate-600 font-bold uppercase tracking-widest text-xs block mb-2">Document Preview Area</Text>
              <Text className="text-slate-700 text-[10px] max-w-xs">Select a document from the list above to preview the identity verification file here.</Text>
            </div>
          </Card>
        </Col>
      </Row>

      <style jsx="true">{`
        .ant-breadcrumb li:last-child {
          color: #6366f1 !important;
        }
        .ant-breadcrumb .ant-breadcrumb-separator {
          color: rgba(255, 255, 255, 0.1) !important;
        }
      `}</style>
    </div>
  );
};

export default KYCDetail;

const Link = ({ children, to, className }) => (
  <a href={to} className={className}>{children}</a>
);

